import { Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';

/**
 * What the card needs. Pages hold their own post shapes (the API's `FrontendBlog`, page-local view
 * models, static arrays), so each maps into this rather than the card learning every shape.
 */
export interface BlogPostCardData {
  title: string;
  excerpt: string;
  imageUrl: string;
  /** Category label shown as the chip over the image. */
  category: string;
  /** e.g. "3 min read" — printed as-is. */
  readTime: string;
  /** The article's slug. Ignored when `articlePath` is given. */
  slug?: string;
  /**
   * Where the card links. Pages that already hold a router-link array pass it straight through;
   * everything else just supplies `slug` and gets `/blogs/<slug>`.
   */
  articlePath?: string | string[];
  author?: string;
  /** Author profile route; omit to leave the name unlinked. */
  authorPath?: string | string[];
  /** Optional store badge in the image's lower-right corner. */
  storeLogoUrl?: string;
}

/** Longer titles are clamped rather than allowed to push the cards out of alignment. */
const TITLE_MAX_LENGTH = 60;

/**
 * The site's blog card — one design for every blog listing, so the blog home, a category, a hub, an
 * author's page and the cross-site "Latest Insights" strip cannot drift apart.
 *
 * Pair it with the grid classes in styles.scss: `.sav-blog-grid` (4 per row) where the listing runs
 * full width, and `.sav-blog-grid.has-sidebar` (3 per row) where a sidebar takes the rest.
 */
@Component({
  selector: 'app-blog-post-card',
  imports: [RouterLink],
  templateUrl: './blog-post-card.html',
})
export class BlogPostCard {
  readonly post = input.required<BlogPostCardData>();

  protected readonly articlePath = computed(() => {
    const post = this.post();
    return post.articlePath ?? ['/blogs', post.slug ?? ''];
  });

  protected readonly displayTitle = computed(() => {
    const title = (this.post().title ?? '').trim();
    return title.length > TITLE_MAX_LENGTH
      ? `${title.slice(0, TITLE_MAX_LENGTH).trimEnd()}...`
      : title;
  });
}
